/*  ThumbnailUploader — compact inline uploader.
    ────────────────────────────────────────────────────────────
    Compact horizontal layout: small square preview (72×72) + info + actions.
    Uploads via PUT /course-builder/media/upload; previews via
    authorized blob fetch (Emergent storage doesn't allow direct img src).
    Returns the uploaded media_id via `onChange(mediaId)`.
*/
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Image as ImageIcon, X, ArrowClockwise } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";


export default function ThumbnailUploader({ value, onChange, courseId }) {
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileRef = useRef(null);

    // Fetch the current thumbnail (auth-protected) as a blob URL.
    useEffect(() => {
        let cancelled = false;
        let objUrl = null;
        if (!value) { setPreviewUrl(null); return undefined; }
        (async () => {
            try {
                const token = localStorage.getItem("ca_token");
                const resp = await fetch(`${API}/course-builder/media/${value}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) throw new Error("fetch failed");
                const blob = await resp.blob();
                objUrl = URL.createObjectURL(blob);
                if (!cancelled) setPreviewUrl(objUrl);
            } catch {
                if (!cancelled) setPreviewUrl(null);
            }
        })();
        return () => {
            cancelled = true;
            if (objUrl) URL.revokeObjectURL(objUrl);
        };
    }, [value]);

    const onPick = () => fileRef.current?.click();

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\//.test(f.type)) {
            toast.error("Please pick an image (PNG / JPG / WebP).");
            e.target.value = "";
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("Max 10 MB for thumbnails.");
            e.target.value = "";
            return;
        }
        const form = new FormData();
        form.append("file", f);
        form.append("kind", "thumbnail");
        if (courseId) form.append("course_id", courseId);
        try {
            setUploading(true);
            const { data } = await api.post("/course-builder/media/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange?.(data.id);
            toast.success("Thumbnail uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const onClear = () => onChange?.(null);

    return (
        <div className="relative" data-testid="thumbnail-uploader">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="thumbnail-file-input" />

            {previewUrl ? (
                <div
                    className="flex items-center gap-3 p-2 rounded-lg border border-border bg-secondary/30"
                    data-testid="thumbnail-preview"
                >
                    <div className="relative w-16 h-16 shrink-0 rounded-md overflow-hidden border border-border bg-secondary">
                        <img src={previewUrl} alt="Course thumbnail" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-foreground">Thumbnail uploaded</div>
                        <div className="text-[10.5px] text-muted-foreground mt-0.5">1200×630 recommended · PNG/JPG/WebP</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <button
                            type="button"
                            onClick={onPick}
                            disabled={uploading}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border bg-background text-xs font-semibold text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                            data-testid="thumbnail-replace-btn"
                            title="Replace"
                        >
                            <ArrowClockwise size={11} weight="bold" /> Replace
                        </button>
                        <button
                            type="button"
                            onClick={onClear}
                            disabled={uploading}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-50"
                            data-testid="thumbnail-clear-btn"
                            title="Remove"
                            aria-label="Remove thumbnail"
                        >
                            <X size={12} weight="bold" />
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={onPick}
                    disabled={uploading}
                    className="w-full flex items-center gap-3 p-2 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors text-left disabled:opacity-60"
                    data-testid="thumbnail-upload-btn"
                >
                    <div className="w-16 h-16 shrink-0 rounded-md border border-border bg-secondary grid place-items-center text-muted-foreground">
                        {uploading ? (
                            <UploadSimple size={20} weight="bold" className="animate-pulse text-primary" />
                        ) : (
                            <ImageIcon size={22} weight="duotone" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-foreground">
                            {uploading ? "Uploading…" : "Click to upload thumbnail"}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-0.5">
                            PNG / JPG / WebP · max 10 MB · 1200×630 recommended
                        </div>
                    </div>
                    <div className="shrink-0">
                        <div className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-primary text-primary-foreground text-xs font-bold">
                            <UploadSimple size={11} weight="bold" /> Upload
                        </div>
                    </div>
                </button>
            )}
        </div>
    );
}
